import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import type { LngLatBounds, Map as MapboxMap } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useBrowserStore,
  useEditingIngredientStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import type {
  MapboxData,
  MapboxMarker,
  LatLongPoint,
} from '@apps/main/new-desktop/modules/newMaps';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import type { Marker } from '@libs/maps';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  HighlightedHotel,
  HotelMapCardData,
  HotelMapCardType,
} from '../../models';
import { useHotelSelectionStore } from '../hotelSelection/hotelSelectionStore';

import {
  getHighlightedHotel,
  getHotelMapCardData,
} from './hotelSelectionMapStoreLogic';

type MarkerType =
  | 'normal'
  | 'inspected'
  | 'selected'
  | 'selected-inspected'
  | 'previously-seen';

const ZOOM_DELAY_MS = 2000;

export const useHotelSelectionMapStore = defineStore(
  'HotelSelectionMapStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const hotelSelectionStore = useHotelSelectionStore();
    const editingIngredientStore = useEditingIngredientStore();
    const statsigEventsStore = useStatsigEventsStore();
    const routerStore = useRouterStore();
    const mapStore = useMapStore();
    const browserStore = useBrowserStore();

    // State
    const highlightedHotel = ref<HighlightedHotel | undefined>(undefined);
    const isFullScreen = ref<boolean>(false);
    const savedBounds = ref<LngLatBounds | undefined>(undefined);
    const mapRef = ref<MapboxMap>();

    // Actions
    function resetSavedBound(): void {
      savedBounds.value = undefined;
    }

    function zoomOnMarkerBounds(): void {
      const bounds = new mapboxgl.LngLatBounds();
      setTimeout(() => {
        const markers = mapboxProperties.value.markers;
        markers.forEach((marker) => {
          bounds.extend([marker.point.longitude, marker.point.latitude]);
        });
        mapRef.value?.fitBounds(bounds, { padding: 24, duration: 1000 });
      }, ZOOM_DELAY_MS);
    }

    function onToggleFullScreen(value: boolean): void {
      isFullScreen.value = value;
    }

    function showHotelMapCard(
      ingredientId: IngredientId,
      hotelCode: string
    ): void {
      const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);
      highlightedHotel.value = {
        ingredientId: ingredientId,
        ingredientIndex,
        hotelCode: hotelCode,
        showHotelMapCard: true,
        position: undefined,
      };
      statsigEventsStore.logHotelSelectionMapMarkerClicked({
        mapSize: hotelMapCardType.value,
        hotelCode: hotelCode,
      });
    }

    async function hotelCardClicked(): Promise<void> {
      if (!highlightedHotel.value || !hotelMapCardData.value) {
        throw new Error('Not possible!');
      }

      const hotelId = highlightedHotel.value;
      const ingredientIndex = recipeStore.getIngredientIndex(
        hotelId.ingredientId
      );
      await routerStore.navigateToHotelDetailsInHotelSelection(
        { ingredientIndex },
        hotelId.hotelCode
      );
    }

    function hideHotelCard(): void {
      highlightedHotel.value = undefined;
    }

    function hightlightOnMap(data: {
      hotelCode: string | undefined;
      position: LatLongPoint | undefined;
    }): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;

      if (ingredientId) {
        const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);
        highlightedHotel.value = getHighlightedHotel(
          data.hotelCode,
          ingredientId,
          ingredientIndex,
          data.position
        );
      }
    }

    function destroyMap(): void {
      mapStore.updateMapShouldCenterOnMarkers(true);
    }

    const inspectedRef = ref();
    const previouslySeen = ref<Record<string, boolean>>({});

    function inspect(ingredientId: IngredientId, hotelCode: string): void {
      inspectedRef.value = { ingredientId, hotelCode };
      previouslySeen.value[`${ingredientId}${hotelCode}`] = true;
    }

    function resetInspect(): void {
      inspectedRef.value = undefined;
    }

    // Getters
    const mapboxProperties = computed<MapboxData>(() => {
      if (!mapStore.markers.length) {
        return {
          markers: [],
          selectedMarker: undefined,
          highlightMarker: highlightedHotel.value?.position,
        };
      }

      return mapStore.markers.reduce<MapboxData>(
        (acc, m: Marker) => {
          const isSelected =
            hotelSelectionStore.selectedHotelCode === m.data.hotelCode;
          const isInspected =
            inspectedRef.value?.ingredientId === m.data.ingredientId &&
            inspectedRef.value?.hotelCode === m.data.hotelCode;

          if (isSelected && m.useContentFormatter === 'hotel-marker') {
            const state: MarkerType = isInspected
              ? 'selected-inspected'
              : 'selected';
            const marker: MapboxMarker = {
              hotelCode: m.data.hotelCode,
              ingredientId: m.data.ingredientId,
              point: m.position,
              label: m.data.formattedPrice,
              state,
            };
            return {
              markers: acc.markers,
              selectedMarker: marker,
              highlightMarker: highlightedHotel.value?.position,
            };
          } else {
            let state: MarkerType = 'normal';
            if (
              previouslySeen.value[`${m.data.ingredientId}${m.data.hotelCode}`]
            ) {
              state = 'previously-seen';
            }

            if (isInspected) {
              state = 'inspected';
            }

            const marker: MapboxMarker = {
              hotelCode: m.data.hotelCode,
              ingredientId: m.data.ingredientId,
              point: m.position,
              label: m.data.formattedPrice,
              state,
            };

            return {
              markers: [...acc.markers, marker],
              selectedMarker: acc.selectedMarker,
              highlightMarker: highlightedHotel.value?.position,
            };
          }
        },
        {
          markers: [],
          selectedMarker: undefined,
          highlightMarker: highlightedHotel.value?.position,
        }
      );
    });

    const hotelMapCardData = computed<HotelMapCardData | undefined>(() =>
      getHotelMapCardData(
        highlightedHotel.value,
        tripStore.getStaticInfo,
        getImageUrl
      )
    );

    const hotelMapCardType = computed<HotelMapCardType>(() => {
      if (browserStore.isSmallerThan900px) {
        return 'mobile';
      }

      if (isFullScreen.value) {
        return 'desktopExtended';
      }

      return 'desktopCollapsed';
    });

    return {
      // State
      highlightedHotel,
      isFullScreen: readonly(isFullScreen),
      savedBounds,
      mapRef,

      // Actions
      onToggleFullScreen,
      hotelCardClicked,
      hideHotelCard,
      hightlightOnMap,
      destroyMap,
      showHotelMapCard,
      inspect,
      resetInspect,
      resetSavedBound,
      zoomOnMarkerBounds,

      // Getters
      mapboxProperties,
      hotelMapCardData,
      hotelMapCardType,
    };
  }
);
